import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import Layout from './layouts/Layout';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import LeadMagnetModal from './components/LeadMagnetModal';
import GoogleAnalytics from './components/GoogleAnalytics';
import { pageTransition } from './utils/animations';

// Eager load Home (always needed)
import Home from './pages/Home';

// Lazy load all other routes for code splitting
const Services = lazy(() => import('./pages/Services'));
const DigitalGrowth = lazy(() => import('./pages/services/DigitalGrowth'));
const WebDevelopment = lazy(() => import('./pages/services/WebDevelopment'));
const Branding = lazy(() => import('./pages/services/Branding'));
const Photography = lazy(() => import('./pages/services/Photography'));
const PrintDesign = lazy(() => import('./pages/services/PrintDesign'));
const Pricing = lazy(() => import('./pages/Pricing'));
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Page loader for Suspense fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  }}>
    <div className="spinner"></div>
  </div>
);

// Animated routes wrapper for page transitions
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        {...pageTransition}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/digital-growth" element={<DigitalGrowth />} />
            <Route path="/services/web-development" element={<WebDevelopment />} />
            <Route path="/services/branding" element={<Branding />} />
            <Route path="/photography" element={<Photography />} />
            <Route path="/print" element={<PrintDesign />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Custom event listener for manual triggers (e.g. clicking 'Free Audit' buttons)
    const handleManualOpen = () => setIsModalOpen(true);
    window.addEventListener('openLeadMagnet', handleManualOpen);

    if (sessionStorage.getItem('hasSeenLeadMagnet')) return;

    let timer;
    const triggerModal = () => {
      if (!sessionStorage.getItem('hasSeenLeadMagnet')) {
        setIsModalOpen(true);
        sessionStorage.setItem('hasSeenLeadMagnet', 'true');
      }
    };

    // 1. Time trigger: 20 seconds (less aggressive for better UX)
    timer = setTimeout(() => {
      triggerModal();
    }, 20000);

    // 2. Scroll trigger: Bottom of page
    const handleScroll = () => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
        triggerModal();
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(timer);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('openLeadMagnet', handleManualOpen);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <GoogleAnalytics />
          <ScrollToTop />
          <Layout>
            <AnimatedRoutes />
          </Layout>
          <LeadMagnetModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
